import React, { useEffect, useMemo, useState } from 'react';
import ToDoAssigneeDropdown from './ToDoAssigneeDropdown';
import ToDoCategoryDropdown from './ToDoCategoryDropdown';
import ToDoStatusDropdown from './ToDoStatusDropdown';

function ToDoForm({
  mode,
  initialTodo,
  statuses,
  categories,
  users,
  usersLoading,
  onSave,
  onCancel,
}) {
  const defaultStatus = useMemo(() => statuses?.[0]?.id || 'open', [statuses]);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState(defaultStatus);
  const [category, setCategory] = useState('');
  const [assignee, setAssignee] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!initialTodo) {
      setName('');
      setDescription('');
      setStatus(defaultStatus);
      setCategory('');
      setAssignee(null);
      return;
    }
    setName(initialTodo.name || '');
    setDescription(initialTodo.description || '');
    setStatus(initialTodo.status || defaultStatus);
    setCategory(initialTodo.category || '');
    setAssignee(initialTodo.assignee ?? null);
  }, [initialTodo, defaultStatus]);

  async function handleSave() {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      setSaving(true);
      await onSave?.({
        name: trimmed,
        description,
        status,
        category,
        assignee,
      });
      if (mode === 'create') {
        setName('');
        setDescription('');
        setStatus(defaultStatus);
        setCategory('');
        setAssignee(null);
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-slate-50 border rounded-lg p-3">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
        <div className="md:col-span-4">
          <label className="text-xs text-neutral-600">Name</label>
          <input
            className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Task name"
            disabled={saving}
          />
        </div>

        <div className="md:col-span-3">
          <label className="text-xs text-neutral-600">Status</label>
          <div className="mt-1">
            <ToDoStatusDropdown value={status} statuses={statuses} onChange={setStatus} disabled={saving} />
          </div>
        </div>

        <div className="md:col-span-3">
          <label className="text-xs text-neutral-600">Category</label>
          <div className="mt-1">
            <ToDoCategoryDropdown value={category} categories={categories} onChange={setCategory} disabled={saving} />
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="text-xs text-neutral-600">Assignee</label>
          <div className="mt-1">
            <ToDoAssigneeDropdown
              value={assignee}
              users={users}
              loading={usersLoading}
              onChange={setAssignee}
              disabled={saving}
            />
          </div>
        </div>

        <div className="md:col-span-12">
          <label className="text-xs text-neutral-600">Description</label>
          <textarea
            className="mt-1 w-full border rounded-lg px-2 py-2 text-sm min-h-[4.5rem] focus:outline-none focus:ring-2 focus:ring-slate-300"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional notes"
            disabled={saving}
          />
        </div>
      </div>

      <div className="mt-3 flex items-center justify-end gap-2">
        {onCancel && (
          <button
            type="button"
            className="px-3 py-2 rounded-lg text-sm border bg-white hover:bg-slate-50 transition disabled:opacity-60"
            onClick={() => !saving && onCancel()}
            disabled={saving}
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          className="px-3 py-2 rounded-lg text-sm bg-slate-900 text-white hover:bg-slate-800 transition disabled:opacity-60"
          onClick={handleSave}
          disabled={saving || !name.trim()}
        >
          {saving ? 'Saving…' : mode === 'create' ? 'Add' : 'Save'}
        </button>
      </div>
    </div>
  );
}

export default ToDoForm;

